import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { API_URL } from '../config/api';

const AdminPanel = () => {
    const [stars, setStars] = useState([]);
    const [totalWeeksLoved, setTotalWeeksLoved] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchStars();
        fetchTotalWeeksLoved();
    }, []);

    const fetchStars = async () => {
        try {
            const response = await fetch(`${API_URL}/api/admin/stars`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
                }
            });
            
            if (!response.ok) {
                throw new Error('Failed to fetch stars');
            }
            
            const data = await response.json();
            setStars(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchTotalWeeksLoved = async () => {
        try {
            const response = await fetch(`${API_URL}/api/stats/total-weeks`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
                }
            });
            
            if (!response.ok) {
                throw new Error('Failed to fetch total weeks loved');
            }
            
            const data = await response.json();
            setTotalWeeksLoved(data.totalWeeks);
        } catch (err) {
            setError(err.message);
        }
    };  

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this star?')) {
            try {
                const response = await fetch(`${API_URL}/api/admin/stars/${id}`, {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
                    }
                });
                
                if (!response.ok) {
                    throw new Error('Failed to delete star');
                }
                
                // Refresh the stars list
                fetchStars();
            } catch (err) {
                setError(err.message);
            }
        }
    };

    if (loading) return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="text-xl">Loading...</div>
        </div>
    );

    if (error) return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="text-red-500 text-xl">Error: {error}</div>
        </div>
    );

    return (
        <>
            <Helmet>
                <title>Admin Panel - Still Loved</title>
                <meta name="robots" content="noindex, nofollow" />
                <meta name="googlebot" content="noindex, nofollow" />
                <meta name="bingbot" content="noindex, nofollow" />
                <meta name="yandex" content="none" />
            </Helmet>
            
            <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-4xl mx-auto">
                    <h1 className="text-3xl font-extrabold text-gray-900 mb-4 text-center">
                        Star Management
                    </h1>
                    
                    <div className="text-center mb-8 flex justify-center gap-4">
                        <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-indigo-100 text-indigo-800">
                            Total Stars: {stars.length}
                        </span>
                        <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-indigo-100 text-indigo-800">
                            Total Weeks Loved: {totalWeeksLoved.toLocaleString()}
                        </span>
                    </div>
                    
                    <div className="bg-white shadow rounded-lg divide-y divide-gray-200">
                        {stars.map(star => (
                            <div key={star._id} className="p-6 flex items-center justify-between">
                                <div className="space-y-1">
                                    <h3 className="text-lg font-medium text-gray-900">{star.name}</h3>
                                    <p className="text-sm text-gray-500">Date: {new Date(star.dob).toLocaleDateString()}</p>
                                    <p className="text-sm text-gray-500">Place: {star.pob}</p>
                                    {star.message && (
                                        <p className="text-sm text-gray-600">Message: {star.message}</p>
                                    )}
                                    <p className="text-xs text-gray-400">
                                        Created: {new Date(star.createdAt).toLocaleString()}
                                    </p>
                                </div>
                                <button 
                                    onClick={() => handleDelete(star._id)}
                                    className="ml-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                >
                                    Delete
                                </button>
                            </div>
                        ))}
                        {stars.length === 0 && (
                            <div className="p-6 text-center text-gray-500">
                                No stars found
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default AdminPanel; 